// ngrokSkipWarning.js
// ─────────────────────────────────────────────────────────────────────────────
// DEV-ONLY BEHAVIOUR, SAFE TO COMMIT. This module is a no-op unless
// REACT_APP_API_BASE_URL actually points at an ngrok host — see isNgrokBackend
// below, which returns early on the very first line of the installer. On
// production (srvr.rxpt.ai / truet.net) nothing here runs, so there is nothing
// to strip before pushing and no way to forget.
// ─────────────────────────────────────────────────────────────────────────────
//
// ngrok's free tier answers any request carrying a browser User-Agent with an
// interstitial warning page (ERR_NGROK_6024) instead of proxying it to the
// backend. That page is a 200 with `Content-Type: text/html` and NO
// `Access-Control-Allow-Origin` header, so the browser rejects it as
// "blocked by CORS policy" — even though the backend's own CORS config is
// correct and curl sees the right headers. Sending the
// `ngrok-skip-browser-warning` header on the request bypasses the interstitial.
//
// The giveaway in DevTools is `net::ERR_FAILED 200 (OK)`: a real CORS
// misconfiguration on our backend would not return 200, and a real network
// failure would not return a status at all.
//
// Rather than edit the ~300 axios/fetch call sites, install it globally here.
// Two deliberate restrictions:
//   1. Only when the API base URL actually points at an ngrok host, so
//      non-ngrok deployments (srvr.rxpt.ai, truet.net) don't start paying for
//      an extra CORS preflight on every request.
//   2. Only for requests aimed at our own API origin. A custom header forces a
//      preflight, and third-party endpoints (ipinfo.io, ipwho.is, ipapi.co)
//      don't list it in Access-Control-Allow-Headers — adding it there would
//      break calls that work today.
import axios from 'axios';

const HEADER = 'ngrok-skip-browser-warning';

const apiOrigin = (() => {
  try {
    return new URL(
      process.env.REACT_APP_API_BASE_URL || 'http://localhost:2512/api'
    ).origin;
  } catch {
    return '';
  }
})();

const isNgrokBackend = /(^|\.)ngrok(-free)?\.(app|dev|io)$/i.test(
  apiOrigin ? new URL(apiOrigin).hostname : ''
);

// A relative URL (or one we can't parse) is going through an axios instance's
// baseURL, which is our API — so it counts as ours.
const targetsApi = (url) => {
  if (!url) return true;
  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === apiOrigin;
  } catch {
    return true;
  }
};

export default function installNgrokSkipWarning(...axiosInstances) {
  if (!isNgrokBackend) return;

  for (const instance of [axios, ...axiosInstances]) {
    if (!instance?.interceptors?.request) continue;
    instance.interceptors.request.use((config) => {
      // config.url is relative when the instance carries a baseURL; join them
      // so an absolute third-party URL is still recognised as third-party.
      const full = config.baseURL && config.url && !/^https?:\/\//i.test(config.url)
        ? `${config.baseURL}${config.url}`
        : config.url;
      if (targetsApi(full)) {
        config.headers = config.headers || {};
        config.headers[HEADER] = 'true';
      }
      return config;
    });
  }

  const nativeFetch = window.fetch?.bind(window);
  if (!nativeFetch || window.fetch.__ngrokPatched) return;

  const patched = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!targetsApi(url)) return nativeFetch(input, init);

    const headers = new Headers(
      init.headers || (typeof input === 'object' ? input?.headers : undefined)
    );
    headers.set(HEADER, 'true');
    return nativeFetch(input, { ...init, headers });
  };
  patched.__ngrokPatched = true;
  window.fetch = patched;
}

/** Lets callers/devtools confirm whether the patch actually engaged. */
export const isNgrokSkipActive = () => isNgrokBackend;
